/**
 * Servicio para la gestión de tipos de documentos
 * Fecha: 16 Oct 2025
 */

import { apiRequest, API_URL } from '@/shared/lib/apiUtils';
import type {
  DocumentType,
  DocumentTypeFilters,
  CreateDocumentTypeDto,
  UpdateDocumentTypeDto,
} from '@/types/employee.types';

/**
 * Servicio de tipos de documentos con todos los endpoints disponibles
 */
export const documentTypesService = {
  /**
   * Listar tipos de documentos con filtros opcionales
   */
  getAll: async (filters?: DocumentTypeFilters): Promise<DocumentType[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const queryString = params.toString();
    const url = queryString
      ? `${API_URL}/document-types?${queryString}`
      : `${API_URL}/document-types`;
    return apiRequest<DocumentType[]>(url);
  },

  /**
   * Obtener solo tipos de documentos obligatorios
   */
  getObligatory: async (): Promise<DocumentType[]> => {
    return apiRequest<DocumentType[]>(`${API_URL}/document-types/obligatory`);
  },

  /**
   * Obtener tipos de documentos por categoría
   */
  getByCategory: async (categoria: string): Promise<DocumentType[]> => {
    return apiRequest<DocumentType[]>(`${API_URL}/document-types/category/${categoria}`);
  },

  /**
   * Obtener tipo de documento por ID
   */
  getById: async (id: number): Promise<DocumentType> => {
    return apiRequest<DocumentType>(`${API_URL}/document-types/${id}`);
  },

  /**
   * Crear nuevo tipo de documento
   */
  create: async (data: CreateDocumentTypeDto): Promise<DocumentType> => {
    return apiRequest<DocumentType>(`${API_URL}/document-types`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Actualizar tipo de documento
   */
  update: async (id: number, data: UpdateDocumentTypeDto): Promise<DocumentType> => {
    return apiRequest<DocumentType>(`${API_URL}/document-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Activar tipo de documento
   */
  activate: async (id: number): Promise<DocumentType> => {
    return apiRequest<DocumentType>(`${API_URL}/document-types/${id}/activate`, {
      method: 'POST',
    });
  },

  /**
   * Desactivar tipo de documento
   */
  deactivate: async (id: number): Promise<DocumentType> => {
    return apiRequest<DocumentType>(`${API_URL}/document-types/${id}/deactivate`, {
      method: 'POST',
    });
  },
};
